import { useCallback, useEffect, useState } from 'react';
import { PublicKey } from '@solana/web3.js';
import {
  EVORE_PROGRAM_ID,
  ORE_TREASURY_ADDRESS,
  computeClaimableOre,
  decodeManager,
  decodeOreMinerSnapshot,
  decodeOreTreasurySnapshot,
  getManagedMinerAuthPda,
  getOreMinerPda,
  inferLiveRefinedOre,
  managerAccountsFilter,
} from '../lib/evore';
import { getConnection } from '../lib/rpc';

export interface Subaccount {
  manager: PublicKey;
  managedMinerAuth: PublicKey;
  authority: PublicKey;
  /** Lamports held in the MMA — withdrawing closes the account, returning all of this. */
  mmaLamports: bigint;
  /** On-chain refined ORE (stale between checkpoints). Kept for reference; UI should
   *  prefer `liveRefinedOre` which folds in the treasury-factor-inferred accrual. */
  refinedOre: bigint;
  /** Refined ORE adjusted for accrual since the miner's last checkpoint. */
  liveRefinedOre: bigint;
  /** Unrefined / pending ORE rewards (smallest units, 11 decimals). */
  rewardsOre: bigint;
  /** What the user receives if they claim right now: 90% of rewardsOre + liveRefinedOre. */
  claimableOre: bigint;
}

export interface SubaccountsState {
  loading: boolean;
  error: string | null;
  subaccounts: Subaccount[];
  /** Wall-clock time of the most recent successful load. null until the first load completes. */
  lastUpdatedAt: Date | null;
  refresh: () => Promise<void>;
}

export function useSubaccounts(authority: PublicKey | null): SubaccountsState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subaccounts, setSubaccounts] = useState<Subaccount[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!authority) {
      setSubaccounts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const connection = getConnection();

      const managers = await connection.getProgramAccounts(
        EVORE_PROGRAM_ID,
        managerAccountsFilter(authority)
      );

      if (managers.length === 0) {
        setSubaccounts([]);
        setLastUpdatedAt(new Date());
        return;
      }

      const enriched = managers.map(({ pubkey, account }) => {
        const decoded = decodeManager(account.data);
        const [mma] = getManagedMinerAuthPda(pubkey, 0n);
        const [oreMiner] = getOreMinerPda(mma);
        return { manager: pubkey, authority: decoded.authority, mma, oreMiner };
      });

      // Treasury goes last in the batch so the (mma, miner) pairing math stays
      // simple. Single RPC call regardless of subaccount count.
      const accountsToFetch = [
        ...enriched.flatMap((e) => [e.mma, e.oreMiner]),
        ORE_TREASURY_ADDRESS,
      ];
      const infos = await connection.getMultipleAccountsInfo(accountsToFetch);
      const treasuryInfo = infos[infos.length - 1];

      const treasury = treasuryInfo
        ? decodeOreTreasurySnapshot(treasuryInfo.data)
        : null;

      const result: Subaccount[] = enriched.map((e, i) => {
        const mmaInfo = infos[i * 2];
        const minerInfo = infos[i * 2 + 1];

        const mmaLamports = mmaInfo ? BigInt(mmaInfo.lamports) : 0n;

        let refinedOre = 0n;
        let liveRefinedOre = 0n;
        let rewardsOre = 0n;
        let claimableOre = 0n;

        if (minerInfo && minerInfo.data && minerInfo.data.length >= 752) {
          try {
            const snapshot = decodeOreMinerSnapshot(minerInfo.data);
            refinedOre = snapshot.refinedOre;
            rewardsOre = snapshot.rewardsOre;
            // Fall back to the stale on-chain value if treasury fetch failed —
            // matches BalanceBatchService.ts:775-791 main-app behavior.
            liveRefinedOre = treasury
              ? inferLiveRefinedOre(snapshot, treasury)
              : refinedOre;
            claimableOre = treasury
              ? computeClaimableOre(snapshot, treasury)
              : (rewardsOre * 9n) / 10n + refinedOre;
          } catch {
            // leave at 0
          }
        }

        return {
          manager: e.manager,
          managedMinerAuth: e.mma,
          authority: e.authority,
          mmaLamports,
          refinedOre,
          liveRefinedOre,
          rewardsOre,
          claimableOre,
        };
      });

      result.sort((a, b) => Number(b.mmaLamports - a.mmaLamports));
      setSubaccounts(result);
      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error('[useSubaccounts] load failed:', err);
      // Error string may be either a translation key (sunset.errors.*) or a
      // raw RPC error — t() in the consuming component falls through to the
      // raw string when the key isn't found, so both render cleanly.
      setError(err instanceof Error ? err.message : 'sunset.errors.loadSubaccountsFailed');
      setSubaccounts([]);
    } finally {
      setLoading(false);
    }
  }, [authority]);

  useEffect(() => {
    void load();
  }, [load]);

  return { loading, error, subaccounts, lastUpdatedAt, refresh: load };
}
